import React, {useEffect, useState} from "react";
import styles from "./SearchForm.module.css";
import {
  Checkbox,
  DefaultButton,
  Dropdown,
  IStackStyles,
  Icon,
  Label,
  PrimaryButton,
  Stack,
  StackItem,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import strings from "../../loc/strings";
import {DatePickerPropsOut, DateProps, FnpDatePicker} from "../Utils";
import {stringToDate} from "../Utils";
import {SearchCriteria} from "../../model/SearchCriteria";
import {TypeOption} from "../../model/TypeOption";

interface SearchFormProps {
  typeOptions: TypeOption[];
  selectedKey: string;
  _handleDropDownChange: (event: any, item: any) => void;
  _handleSearchItem: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => void;
  searchCriteria: SearchCriteria;
  _handleDateChange: (date: Date | null | undefined, name: string) => void;
  _isExactMatch: () => void;
  isExactOnly: boolean;
  _isAkaEnabled: () => void;
  isAkaEnabled: boolean;
  _onSearchClick: () => void;
  _onReset: () => void;
  isDisabled: () => boolean;
  isGroupNaturalRequired: () => boolean;
  isGroupLegalRequired: () => boolean;
  _isDateValid?: any;
  // errorMessage?: any;
  dateOfBirth?: any;
  setDateString?: any;
  dateString?: any;
  hasErrorMessage?: any;
  resetFlag?: boolean;
}

const stackStylesCheckBox: Partial<IStackStyles> = {
  root: {
    marginBottom: "5px",
  },
};

const datePickerStyle = {
  root: {
    selectors: {
      '& div[class*="statusMessage-"]': {
        marginTop: "0px",
      },
      '& div[role="alert"]': {
        position: "absolute",
      },
    },
  },
  statusMessage: {
    display: 'none'
  }
};

const iconStyles: Partial<IStackStyles> = {
  root: {
    marginLeft: "8px"
  },
};

function SearchForm(props: Readonly<SearchFormProps>) {

  const [dateOfBirthState, setDateOfBirthState] = useState<DateProps>({value: props.dateOfBirth, isValid: true, isFutureDate: false});

  useEffect(() => {
    if(props.dateOfBirth === undefined) {
      setDateOfBirthState({value: props.dateOfBirth, isValid: true, isFutureDate: false, isPastDate: false});
    }
  }, [props.dateOfBirth]);

  // search on enter
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!props.isDisabled()) {
      props._onSearchClick();
    }
  };


  return (
      <form onSubmit={handleSubmit}>
        <button type="submit" style={{ display: 'none' }} />
      {props.selectedKey === props.typeOptions[0].key && (
          <Stack className={styles.SearchStack}>
            <StackItem>
              <Stack className={styles.StackGrid} horizontal>
                <StackItem>
                  <Dropdown
                      options={props.typeOptions}
                      label={strings.typeLabel}
                      selectedKey={props.selectedKey}
                      onChange={props._handleDropDownChange}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.firstName}
                      label={strings.firstNameLabel}
                      name={strings.firstNameName}
                      placeholder={strings.poiPlaceholder}
                      required={props.isGroupNaturalRequired()}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.lastName}
                      label={strings.lastNameLabel}
                      name={strings.lastNameName}
                      placeholder={strings.poiPlaceholder}
                      required={props.isGroupNaturalRequired()}
                  />
                </StackItem>
                <Stack className={styles.DoubleFieldVAlignBottom}>
                  <StackItem>
                    <TextField
                        onChange={props._handleSearchItem}
                        value={props.searchCriteria.alsoKnownAs}
                        label={strings.alsoKnownAsLabel}
                        name={strings.alsoKnownAsName}
                        placeholder={strings.poiPlaceholder}
                    />
                  </StackItem>
                  <Stack horizontal verticalAlign="center">
                    <Checkbox
                        styles={stackStylesCheckBox}
                        label={strings.akaCheckbox}
                        onChange={props._isAkaEnabled}
                        checked={props.isAkaEnabled}
                    />
                    <TooltipHost content={strings.akaTooltip}>
                      <Icon iconName="Info" styles={iconStyles} />
                    </TooltipHost>
                  </Stack>
                </Stack>
                <StackItem>
                  <Stack className={styles.DoubleFieldVAlignBottom}>
                    <StackItem>
                      <Label>{strings.placeOfBirthLabel}</Label>
                      <TextField
                          onChange={props._handleSearchItem}
                          value={props.searchCriteria.countryOfBirth}
                          ariaLabel={strings.placeOfBirthLabel}
                          name={strings.countryOfBirthName}
                          placeholder={strings.placeOfBirthCountryPlaceholder}
                      />
                    </StackItem>
                    <StackItem>
                      <Label styles={{ root: { color: 'transparent' } }}>&nbsp;</Label>
                      <TextField
                          onChange={props._handleSearchItem}
                          value={props.searchCriteria.cityOfBirth}
                          ariaLabel={strings.cityOfBirthLabel}
                          name={strings.cityOfBirthName}
                          placeholder={strings.placeOfBirthCityPlaceholder}
                      />
                    </StackItem>
                  </Stack>
                </StackItem>
                <StackItem>
                  <FnpDatePicker
                      datePickerKey={"creationDateFrom"}
                      isRequired={props.isGroupNaturalRequired()}
                      date={dateOfBirthState.value ?? new Date()}
                      resetFlag={props.resetFlag}
                      resetInitDate={true}
                      setSelectedDate={(datePickerProps: DatePickerPropsOut) => {
                        const date = stringToDate(datePickerProps.value);
                        console.log('datePickerProps', datePickerProps, date);
                        setDateOfBirthState( {
                          ...dateOfBirthState,
                          value: date,
                          dateString: datePickerProps.value,
                          isValid: datePickerProps.valid,
                        });
                        props.hasErrorMessage(!datePickerProps.valid);
                        props._handleDateChange(datePickerProps.valid ? date : undefined, strings.dateOfBirthName)
                      }}
                      initialPickerDate={new Date()}
                      label={strings.dateOfBirthLabel}
                      ariaLabel={strings.filterLabelCreationDate}
                      placeholder={strings.datepickerPlaceholder}
                      isOutOfBoundsErrorMessage={"Future dates are not allowed"}
                      styles={datePickerStyle}
                  ></FnpDatePicker>
                </StackItem>
                <StackItem>
                  <Stack className={styles.DoubleFieldVAlignBottom}>
                    <StackItem>
                      <Stack horizontal verticalAlign={"center"}>
                        <StackItem>
                          <Label>{strings.idNumberLabel}</Label>
                        </StackItem>
                        <StackItem>
                          <TooltipHost content={strings.idTooltip}>
                            <Icon iconName="Info" styles={iconStyles} />
                          </TooltipHost>
                        </StackItem>
                      </Stack>
                      <TextField
                          onChange={props._handleSearchItem}
                          value={props.searchCriteria.idNumber}
                          ariaLabel={strings.idNumberLabel}
                          name={strings.idName}
                          placeholder={strings.idPlaceHolder}
                      />
                    </StackItem>
                    <StackItem>
                      <Stack horizontal verticalAlign={"center"}>
                        <StackItem>
                          <Label>{strings.socialSecurityNumberLabel}</Label>
                        </StackItem>
                        <StackItem>
                          <TooltipHost content={strings.ssnTooltip}>
                            <Icon iconName="Info" styles={iconStyles} />
                          </TooltipHost>
                        </StackItem>
                      </Stack>
                      <TextField
                          onChange={props._handleSearchItem}
                          value={props.searchCriteria.socialSecurityNumber}
                          ariaLabel={strings.socialSecurityNumberLabel}
                          name={strings.socialSecurityNumberName}
                          placeholder={strings.numberPlaceholder}
                      />
                    </StackItem>
                  </Stack>
                </StackItem>
                <StackItem className={styles.LastGridItem}>
                  <Stack className={styles.DoubleFieldVAlignCenter} horizontal verticalAlign={"end"} >
                    <StackItem>
                      <Checkbox
                          styles={stackStylesCheckBox}
                          label={strings.searchCheckbox}
                          onChange={props._isExactMatch}
                          checked={props.isExactOnly}
                      />
                    </StackItem>
                    <StackItem>
                      <Stack horizontal horizontalAlign={"end"} tokens={{childrenGap: 10}}>
                        <StackItem>
                          <PrimaryButton
                              text={strings.searchButtonText}
                              onClick={props._onSearchClick}
                              disabled={props.isDisabled()}
                          />
                        </StackItem>
                        <StackItem>
                          <DefaultButton
                              text={strings.resetButtonText}
                              onClick={props._onReset}
                          />
                        </StackItem>
                      </Stack>
                    </StackItem>
                  </Stack>
                </StackItem>
              </Stack>
            </StackItem>
          </Stack>
      )}
      {props.selectedKey === props.typeOptions[1].key && (
          <Stack className={styles.SearchStack}>
            <StackItem>
              <Stack className={styles.StackGrid} horizontal>
                <StackItem>
                  <Dropdown
                      options={props.typeOptions}
                      label={strings.typeLabel}
                      selectedKey={props.selectedKey}
                      onChange={props._handleDropDownChange}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.legalName}
                      label={strings.legalNameLabel}
                      name={strings.legalNameName}
                      placeholder={strings.poiPlaceholder}
                      required={props.isGroupLegalRequired()}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.alsoKnownAs}
                      label={strings.alsoKnownAsLabel}
                      name={strings.alsoKnownAsName}
                      placeholder={strings.poiPlaceholder}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.leiRegistration}
                      label={strings.leiRegistrationNumberLabel}
                      name={strings.leiRegistrationNumberName}
                      placeholder={strings.numberPlaceholder}
                  />
                </StackItem>
                <StackItem>
                  <TextField
                      onChange={props._handleSearchItem}
                      value={props.searchCriteria.countryInc}
                      label={strings.countryOfIncorporationLabel}
                      name={strings.countryOfIncorporationName}
                      placeholder={strings.placeOfBirthCountryPlaceholder}
                      styles={{ fieldGroup: { width: '100%' } }}
                  />
                </StackItem>
                <StackItem className={styles.ExactMatch}>
                  <Checkbox
                      styles={stackStylesCheckBox}
                      label={strings.searchCheckbox}
                      onChange={props._isExactMatch}
                      checked={props.isExactOnly}
                  />
                </StackItem>
                <StackItem className={styles.LastGridItemLegal}>
                  <Stack className={styles.Stack} horizontal horizontalAlign="end" verticalAlign="end" >
                    <StackItem>
                      <PrimaryButton
                          text={strings.searchButtonText}
                          onClick={props._onSearchClick}
                          disabled={props.isDisabled()}
                      />
                    </StackItem>
                    <StackItem>
                      <DefaultButton
                          text={strings.resetButtonText}
                          onClick={props._onReset}
                      />
                    </StackItem>
                  </Stack>
                </StackItem>
              </Stack>
            </StackItem>
          </Stack>
      )}
    </form>
  );
}

export default SearchForm;
